import { z } from 'zod'

import { CountryNameZ, CurrencyZ } from '@/types/common'
import { CountriesCode } from '@/types/locale'

import {
	ExpenseFileZ,
	ExpenseModelZ,
	ExpenseRequestAttendeeZ,
	ExpenseRequestZ,
	ExpenseResponseAttendeeZ,
	ExpenseResponseZ,
	MerchantCountryZ,
	TaxInExpenseZ,
} from './expense'

export const ExpenseOcrStatusZ = z.enum(['processing', 'analysed', 'error'])
export type ExpenseOcrStatus = z.infer<typeof ExpenseOcrStatusZ>

const ExpenseOcrSuggestionsResponseZ = z
	.object({
		amount: z.number(),
		currency: CurrencyZ,
		date: z.iso.date(),
		merchant: z.string(),
		merchant_country_code: z.enum(CountriesCode),
		merchant_country_name: CountryNameZ,
	})
	.partial()
export type ExpenseOcrSuggestionsResponse = z.infer<typeof ExpenseOcrSuggestionsResponseZ>

const ExpenseOcrSuggestionsLocalZ = ExpenseOcrSuggestionsResponseZ.omit({
	amount: true,
	date: true,
	merchant_country_code: true,
	merchant_country_name: true,
})
	.extend({
		amount: z.string(),
		date: z.date(),
		merchantCountry: MerchantCountryZ,
	})
	.partial()
export type ExpenseOcrSuggestionsLocal = z.infer<typeof ExpenseOcrSuggestionsLocalZ>

const ExpenseOcrResultZ = ExpenseOcrSuggestionsResponseZ.extend({ ocr_status: ExpenseOcrStatusZ })
export type ExpenseOcrResult = z.infer<typeof ExpenseOcrResultZ>

export const StandardExpenseModelZ = z
	.object({
		...ExpenseModelZ.shape,
		amount: z.string(),
		attendees: z.array(ExpenseResponseAttendeeZ),
		category: z.string().nullish(),
		date: z.date().nullish(),
		file: ExpenseFileZ.nullish(),
		merchant: z.string().nullish(),
		merchantCountry: MerchantCountryZ.extend({ code: MerchantCountryZ.shape.code.nullable() }),
		ocr: z.object({ suggestions: ExpenseOcrSuggestionsLocalZ, status: ExpenseOcrStatusZ.nullish() }),
		vatRate: TaxInExpenseZ.nullish(),
	})
	.strict()
export type StandardExpenseModel = z.infer<typeof StandardExpenseModelZ>

export const StandardExpenseResponseZ = z
	.object({
		...ExpenseResponseZ.shape,
		...StandardExpenseModelZ.pick({ attendees: true, category: true, merchant: true }).shape,
		amount: z.number().positive().nullish(),
		date: z.iso.date().nullish(),
		file_data: StandardExpenseModelZ.shape.file,
		merchant_country_code: z.enum(CountriesCode).nullable(),
		merchant_country_name: CountryNameZ.nullable(),
		ocr_results: ExpenseOcrSuggestionsResponseZ.nullish(),
		ocr_status: ExpenseOcrStatusZ.nullish(),
		vat_rate: StandardExpenseModelZ.shape.vatRate,
	})
	.strict()
export type StandardExpenseResponse = z.infer<typeof StandardExpenseResponseZ>

export const StandardExpenseRequestZ = z
	.object({
		...ExpenseRequestZ.shape,
		...StandardExpenseResponseZ.pick({
			category: true,
			currency: true,
			expense_report: true,
			file_data: true,
			merchant: true,
			ocr_results: true,
			ocr_status: true,
		}).shape,
		amount: z.number().positive(),
		attendees: z.array(ExpenseRequestAttendeeZ),
		date: z.iso.date(),
		merchant_country_name: CountryNameZ,
		vat_rate_id: z.number().nullable(),
	})
	.strict()
export type StandardExpenseRequest = z.infer<typeof StandardExpenseRequestZ>
